import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { TRAIT_LABELS, TRAITS, PHILOSOPHERS } from "../data/philosophers";
import { PHILOSOPHER_PROFILES } from "../data/philosopherProfiles";
import { gradeFor } from "../lib/scoring";
import { drawShareCard, canvasToBlob, shareOrDownload } from "../lib/shareCard";
import { track } from "../lib/analytics";
import { api } from "../lib/api";
import Footer from "../components/Footer";
import SEOHead from "../components/SEOHead";

const findPhilosopher = (id) => PHILOSOPHERS.find((p) => p.id === id);

export default function Result({ result, resultId, onRetake }) {
  const { user, matches } = result;
  const top = matches[0];
  const [sharing, setSharing] = useState(false);
  const [checkoutBusy, setCheckoutBusy] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailValue, setEmailValue] = useState("");
  const [profileOpen, setProfileOpen] = useState(false);
  const previewRef = useRef(null);
  const cardBlobRef = useRef(null);
  const paywallRef = useRef(null);
  const paywallSeenRef = useRef(false);

  const traitsSorted = [...TRAITS]
    .map((t) => ({ key: t, label: TRAIT_LABELS[t], value: user[t], grade: gradeFor(user[t]) }))
    .sort((a, b) => b.value - a.value);
  const topFour = traitsSorted.slice(0, 4);
  const profile = PHILOSOPHER_PROFILES[top.philosopher.id];

  // Generate share card
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const canvas = drawShareCard({
        philosopher: top.philosopher,
        pct: top.pct,
        topTraits: topFour,
      });
      const blob = await canvasToBlob(canvas);
      if (cancelled) return;
      cardBlobRef.current = blob;
      if (previewRef.current) {
        // Clear any previous canvas via DOM API (safer + linter-friendly than innerHTML).
        while (previewRef.current.firstChild) {
          previewRef.current.removeChild(previewRef.current.firstChild);
        }
        canvas.style.width = "100%";
        canvas.style.height = "auto";
        canvas.style.borderRadius = "10px";
        canvas.style.display = "block";
        previewRef.current.appendChild(canvas);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fire paywall_view when scrolled into view
  useEffect(() => {
    const el = paywallRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !paywallSeenRef.current) {
            paywallSeenRef.current = true;
            track("paywall_view", { philosopher: top.philosopher.id });
          }
        });
      },
      { threshold: 0.4 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [top.philosopher.id]);

  const onShare = async () => {
    if (!cardBlobRef.current || sharing) return;
    setSharing(true);
    track("share_click", { philosopher: top.philosopher.id, pct: top.pct });
    const res = await shareOrDownload(
      cardBlobRef.current,
      `philosopher-test-${top.philosopher.id}.png`,
      `I am ${top.philosopher.name.toUpperCase()} — ${top.pct}% match. Which philosopher are you? thephilosophertest.com`,
    );
    if (res.shared || res.downloaded) {
      track("share_success", {
        philosopher: top.philosopher.id,
        method: res.shared ? "webshare" : "download",
      });
    }
    setSharing(false);
  };

  const onCheckout = async () => {
    if (checkoutBusy || !resultId) return;
    setCheckoutBusy(true);
    setCheckoutError("");
    track("checkout_start", { result_id: resultId });
    try {
      const { checkout_url } = await api.checkout(resultId, window.location.origin);
      window.location.assign(checkout_url);
    } catch (e) {
      console.error(e);
      // Try to surface a human-readable reason from the backend.
      // FastAPI wraps our structured payload under `detail`.
      const detail = e?.response?.data?.detail;
      let msg = "Checkout is temporarily unavailable. Please try again in a moment.";
      if (detail && typeof detail === "object" && detail.message) {
        msg = detail.message;
      } else if (typeof detail === "string" && detail) {
        msg = detail;
      }
      setCheckoutError(msg);
      track("checkout_error", {
        result_id: resultId,
        status: e?.response?.status || 0,
        code: (detail && detail.error) || "unknown",
      });
      setCheckoutBusy(false);
    }
  };

  const onEmailSubmit = async (e) => {
    e.preventDefault();
    if (emailBusy || !resultId || !emailValue) return;
    setEmailBusy(true);
    try {
      await api.captureEmail(emailValue, resultId, "free_result");
      track("email_capture", { result_id: resultId, source: "free_result" });
      setEmailSent(true);
    } catch (err) {
      console.error(err);
    } finally {
      setEmailBusy(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="relative z-10 mx-auto min-h-[100dvh] w-full max-w-md px-6 py-14"
      data-testid="result-screen"
    >
      <SEOHead noindex path={resultId ? `/result/${resultId}` : "/result"} title="Your result" />
      {/* HERO */}
      <div className="text-center">
        <p className="text-[11px] font-medium uppercase" style={{ color: "var(--muted)", letterSpacing: "0.32em" }}>
          You are
        </p>
        <h1
          className="mt-3 font-serif text-[38px] leading-[1.05] sm:text-[46px]"
          style={{ color: "var(--gold)" }}
          data-testid="philosopher-name"
        >
          {top.philosopher.name}
        </h1>
        <p className="mt-2 font-serif italic text-lg" style={{ color: "var(--gold-light)" }}>
          {top.philosopher.title}
        </p>
        <p className="mt-5 font-serif text-4xl" style={{ color: "var(--text)" }} data-testid="top-match-pct">
          {top.pct}
          <span className="ml-1 text-lg align-middle font-sans" style={{ color: "var(--muted)", letterSpacing: "0.18em" }}>
            % MATCH
          </span>
        </p>
      </div>

      {/* Description + quote */}
      <div className="mt-10">
        <p className="text-[15px] leading-relaxed" style={{ color: "var(--text)" }}>
          {top.philosopher.description}
        </p>
        <p
          className="mt-6 border-l-2 pl-4 font-serif italic text-[17px] leading-relaxed"
          style={{ color: "var(--gold-light)", borderColor: "var(--gold)" }}
        >
          “{top.philosopher.quote}”
        </p>
      </div>

      {/* WHO WAS X — enriched profile (FREE) */}
      {profile && (
        <div className="panel mt-8" data-testid="philosopher-profile-panel">
          <button
            className="flex w-full items-center justify-between text-left"
            onClick={() => setProfileOpen((v) => !v)}
            data-testid="profile-toggle-btn"
          >
            <span
              className="text-[10px] font-medium uppercase"
              style={{ color: "var(--gold)", letterSpacing: "0.32em" }}
            >
              Who was {top.philosopher.name.split(" ").slice(-1)[0]}?
            </span>
            <span style={{ color: "var(--gold)" }}>{profileOpen ? "−" : "+"}</span>
          </button>

          {profileOpen && (
            <div className="mt-5 flex flex-col gap-5">
              <div>
                <span
                  className="inline-block rounded-full border px-3 py-1 text-[10px] uppercase"
                  style={{
                    borderColor: "rgba(201,167,85,0.4)",
                    color: "var(--gold-light)",
                    letterSpacing: "0.18em",
                  }}
                >
                  Movement · {profile.movement}
                </span>
              </div>
              <div>
                <p className="text-[10px] font-medium uppercase mb-2" style={{ color: "var(--muted)", letterSpacing: "0.28em" }}>
                  Their view of life
                </p>
                <p className="font-serif italic text-[15px] leading-relaxed" style={{ color: "var(--text)" }}>
                  {profile.view}
                </p>
              </div>
              <div>
                <p className="text-[10px] font-medium uppercase mb-2" style={{ color: "var(--muted)", letterSpacing: "0.28em" }}>
                  Your strengths
                </p>
                <ul className="flex flex-col gap-2">
                  {profile.strengths.map((s) => (
                    <li key={s} className="text-[14px] leading-relaxed" style={{ color: "var(--text)" }}>
                      <span style={{ color: "var(--gold)" }}>·</span> {s}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="text-[10px] font-medium uppercase mb-2" style={{ color: "var(--muted)", letterSpacing: "0.28em" }}>
                  How you move through the world
                </p>
                <p className="text-[14px] leading-relaxed" style={{ color: "var(--text)" }}>
                  {profile.move}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* CHARACTER SHEET */}
      <div className="panel mt-6" data-testid="character-sheet">
        <p className="text-[10px] font-medium uppercase" style={{ color: "var(--gold)", letterSpacing: "0.32em" }}>
          Your character sheet
        </p>
        <div className="mt-5 flex flex-col gap-4">
          {traitsSorted.map((t) => (
            <div key={t.key} className="flex items-center gap-4" data-testid={`trait-row-${t.key}`}>
              <div className="w-24 shrink-0 font-serif text-[15px]" style={{ color: "var(--text)" }}>
                {t.label}
              </div>
              <div className="trait-track flex-1">
                <div className="trait-fill" style={{ width: `${Math.max(4, Math.round(t.value))}%` }} />
              </div>
              <div
                className="w-9 shrink-0 text-left font-serif text-lg tabular-nums"
                style={{ color: "var(--gold-light)" }}
                data-testid={`trait-grade-${t.key}`}
              >
                {t.grade}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* MATCHES */}
      <div className="panel mt-6" data-testid="matches-panel">
        <p className="text-[10px] font-medium uppercase" style={{ color: "var(--gold)", letterSpacing: "0.32em" }}>
          Your matches
        </p>
        <div className="mt-5 flex flex-col">
          {matches.slice(0, 5).map((m, i) => (
            <div
              key={m.philosopher.id}
              className={`flex items-center justify-between py-3 ${i < 4 ? "border-b" : ""}`}
              style={{
                borderColor: "rgba(154,151,164,0.12)",
                color: i === 0 ? "var(--gold)" : "var(--text)",
              }}
              data-testid={`match-row-${i}`}
            >
              <div className="flex items-center gap-2">
                {i === 0 && <span aria-hidden>❦</span>}
                <span className="font-serif text-[16px]">{m.philosopher.name}</span>
              </div>
              <span className="font-serif text-[16px]" data-testid={`match-pct-${i}`}>
                {m.pct}%
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* PAYWALL — FULL REPORT */}
      <div ref={paywallRef} className="panel mt-8" data-testid="paywall-panel">
        <p className="text-[10px] font-medium uppercase" style={{ color: "var(--gold)", letterSpacing: "0.32em" }}>
          Full Report
        </p>
        <h3 className="mt-3 font-serif text-[26px] leading-[1.15]" style={{ color: "var(--text)" }}>
          Your full report
        </h3>
        <p className="mt-2 text-[14px]" style={{ color: "var(--muted)" }}>
          The free result shows who you are. The report shows what to do with it.
        </p>
        <p
          className="mt-1 font-serif italic text-[14px]"
          style={{ color: "var(--gold-light)" }}
          data-testid="paywall-subtitle"
        >
          Your mirror, your shadow, your 30-day plan.
        </p>

        <div className="mt-6 grid grid-cols-1 gap-3">
          {PAYWALL_PREVIEWS.map((p, i) => (
            <div
              key={p.title}
              className="relative overflow-hidden rounded-md border p-4"
              style={{ borderColor: "rgba(201,167,85,0.18)", background: "rgba(22,24,33,0.55)" }}
              data-testid={`paywall-card-${i}`}
            >
              <div className="flex items-center gap-2">
                <LockIcon />
                <p className="font-serif text-[15px]" style={{ color: "var(--gold-light)" }}>
                  {p.title}
                </p>
              </div>
              <p
                className="mt-3 text-[13px] leading-relaxed select-none"
                style={{
                  color: "var(--text)",
                  filter: "blur(5px)",
                  userSelect: "none",
                  WebkitUserSelect: "none",
                }}
                aria-hidden
              >
                {p.blurredBody}
              </p>
            </div>
          ))}
        </div>

        <button
          className="btn-primary mt-6"
          onClick={onCheckout}
          disabled={checkoutBusy || !resultId}
          data-testid="unlock-report-btn"
        >
          {checkoutBusy ? "Opening checkout…" : "Unlock my full report — $9.99"}
        </button>
        {checkoutError && (
          <div
            role="alert"
            className="mt-3 rounded border px-4 py-3 text-[13px] leading-snug"
            style={{
              borderColor: "rgba(220, 100, 100, 0.45)",
              background: "rgba(60, 20, 20, 0.35)",
              color: "#f5c9c9",
            }}
            data-testid="checkout-error"
          >
            {checkoutError}
          </div>
        )}
        <p
          className="mt-3 text-center text-[13px] leading-snug"
          style={{ color: "var(--text)" }}
          data-testid="paywall-anti-objection"
        >
          Not a generic AI report — built from your exact six-trait scores.
        </p>
        <p className="mt-2 text-center text-[11px]" style={{ color: "var(--muted)", letterSpacing: "0.06em" }}>
          One-time payment · Delivered instantly + by email · No account needed
        </p>
      </div>

      {/* ACTIONS */}
      <div className="mt-8 flex flex-col gap-3">
        <button className="btn-primary" onClick={onShare} disabled={sharing} data-testid="share-btn">
          <span aria-hidden style={{ marginRight: 10 }}>📜</span>
          {sharing ? "Preparing…" : "Share my result"}
        </button>

        {/* Email capture (free) */}
        <form onSubmit={onEmailSubmit} className="mt-2 flex flex-col gap-2" data-testid="email-capture-form">
          <label className="text-[10px] font-medium uppercase" style={{ color: "var(--muted)", letterSpacing: "0.28em" }}>
            Email me my results
          </label>
          <div className="flex gap-2">
            <input
              type="email"
              value={emailValue}
              onChange={(e) => setEmailValue(e.target.value)}
              placeholder="you@example.com"
              disabled={emailSent || emailBusy}
              required
              className="flex-1 rounded border px-3 py-3 text-[14px] outline-none"
              style={{
                background: "rgba(22,24,33,0.5)",
                borderColor: "rgba(154,151,164,0.25)",
                color: "var(--text)",
              }}
              data-testid="email-input"
            />
            <button
              type="submit"
              className="btn-ghost"
              style={{ width: "auto", padding: "12px 18px" }}
              disabled={emailSent || emailBusy || !resultId}
              data-testid="email-submit-btn"
            >
              {getEmailButtonLabel(emailSent, emailBusy)}
            </button>
          </div>
          <p
            className="text-[11px]"
            style={{ color: "var(--muted)" }}
            data-testid="email-capture-privacy-note"
          >
            No spam. Unsubscribe anytime.
          </p>
        </form>

        <button className="btn-ghost mt-2" onClick={onRetake} data-testid="retake-btn">
          Retake the test
        </button>
      </div>

      {/* SHARE CARD PREVIEW */}
      <div className="mt-8">
        <p className="mb-3 text-[10px] font-medium uppercase" style={{ color: "var(--muted)", letterSpacing: "0.32em" }}>
          Preview
        </p>
        <div
          ref={previewRef}
          data-testid="share-preview"
          className="overflow-hidden rounded-[10px]"
          style={{ border: "1px solid rgba(201,167,85,0.25)" }}
        />
      </div>

      <p className="mt-10 text-center text-xs" style={{ color: "var(--muted)", letterSpacing: "0.12em" }}>
        thephilosophertest.com
      </p>
      <Footer />
    </motion.div>
  );
}

const PAYWALL_PREVIEWS = [
  {
    title: "Your Shadow — the 3 blind spots of your profile",
    blurredBody:
      "Blind spot one shows up when the room agrees too quickly. Your instinct is to defer, and it costs you sharper decisions in the exact moments they matter most. Second, your discipline becomes a wall others cannot see through. Third pattern...",
  },
  {
    title: "Your Inner Contradictions — where your traits fight each other",
    blurredBody:
      "Your ambition wants motion; your serenity wants stillness. These two do not negotiate — they wrestle nightly. Every time you commit to a large project you also flinch at the friction. The result is a pattern of half-starts...",
  },
  {
    title: "Your Reading Path — 5 books chosen for your exact profile",
    blurredBody:
      "The first book is not what most people expect for your philosopher — it is deliberately chosen because your empathy score is lower than your discipline score and this pairing needs a specific corrective. Read it slowly, one chapter per evening...",
  },
  {
    title: "Your 30-Day Plan — one small practice per week, matched to your weakest trait",
    blurredBody:
      "Week one, ten minutes each morning of a very specific journaling exercise designed to raise the trait you scored lowest on. Week two shifts to a five-minute evening habit. Week three introduces...",
  },
];

function LockIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" style={{ color: "var(--gold)" }}>
      <rect x="4" y="10" width="16" height="11" rx="2" />
      <path d="M8 10V7a4 4 0 0 1 8 0v3" />
    </svg>
  );
}

function getEmailButtonLabel(sent, busy) {
  if (sent) return "Sent ✓";
  if (busy) return "…";
  return "Send";
}

// Re-exposed for App to reuse when hydrating from /result/:id.
export { findPhilosopher };
